import React, { ChangeEvent } from 'react';
import { Stack } from '../stack';
import { BaseText } from '../text';
import { Checkbox } from '../checkbox';
import { IconArrowChevronRight } from '../icons/generated';
import { CategoryEntry } from './types';
import { getColor, getCss } from './styles';

interface CategoryItemProps {
  item: CategoryEntry;
  isSelected: boolean;
  onItemSelect: (item: CategoryEntry) => void;
  onItemClick: (item: CategoryEntry) => void;
  getCheckState: (item: CategoryEntry) => { checked: boolean; indeterminate: boolean };
  multiple?: boolean;
  disabled?: boolean;
  level?: number;
}

export const CategoryItem = ({
  item,
  isSelected,
  onItemSelect,
  onItemClick,
  getCheckState,
  multiple = false,
  disabled = false,
  level = 1,
}: CategoryItemProps) => {
  const isItemDisabled = (disabled || item.disabled) ?? false;
  const { checked, indeterminate } = getCheckState(item);
  const hasChildren = item.children && item.children.length > 0;
  const itemId = `category-item-${item.id}`;

  const handleClick = () => {
    if (isItemDisabled) return;
    onItemClick(item);
  };

  const handleCheckboxClick = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (isItemDisabled) return;
    onItemSelect(item);
  };

  return (
    <Stack
      direction='row'
      className='pds-category-selector-list-item'
      width='100%'
      align='center'
      justify='space-between'
      p={multiple ? '8px 12px 8px 16px' : '8px 16px'}
      onClick={handleClick}
      css={getCss(isItemDisabled, isSelected, checked)}
      role={multiple ? 'checkbox' : 'option'}
      id={itemId}
      tabIndex={isItemDisabled ? -1 : 0}
      aria-selected={multiple ? undefined : isSelected}
      aria-checked={multiple ? (indeterminate ? 'mixed' : checked) : undefined}
      aria-disabled={isItemDisabled}
      aria-expanded={hasChildren ? false : undefined}
      aria-level={level}
    >
      <Stack direction='row' gap={8} align='center' height={24}>
        {multiple && (
          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            disabled={isItemDisabled}
            onChange={handleCheckboxClick}
            unstableFocusVisible={false}
            aria-hidden='true'
            tabIndex={-1}
          />
        )}
        <BaseText
          id={`${itemId}-label`}
          kind='Body_13_Regular'
          color={getColor(isItemDisabled, isSelected, checked, indeterminate, multiple)}
        >
          {item.name}
        </BaseText>
      </Stack>
      {hasChildren && (
        <IconArrowChevronRight color={getColor(isItemDisabled, isSelected, checked, indeterminate, multiple)} />
      )}
    </Stack>
  );
};
